import React, { useState, useEffect } from 'react';
import { History, X, Unlock, Lock } from 'lucide-react';
import './arrondissement.css';
import { useAuth } from "../AuthContext";
import { useNavigate } from "react-router-dom";
import AxiosInstance from "../AxiosInstance";

const PERIODS = [
  { value: 'day', label: 'Jour' },
  { value: 'week', label: 'Semaine' },
  { value: 'month', label: 'Mois' },
  { value: 'campaign', label: 'Campagne' },
];

const formatDate = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
};

const ModeBadge = ({ mode }) => (
  <span className={`mode-badge ${mode === 'free' ? 'mode-free' : 'mode-paid'}`}>
    {mode === 'free' ? 'Gratuit' : 'Payant'}
  </span>
);

const AccessConfig = () => {
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    mode: 'free',
    price: 0,
    campaignStartDate: '',
    campaignEndDate: '',
    freePreviewStepsCount: 0,
    freeProceduresLimit: '',
    freeProceduresPeriod: 'month',
    message: '',
    isActive: true,
  });

  useEffect(() => {
    if (!isAuthenticated) navigate("/");
  }, [isAuthenticated, navigate]);

  const fetchConfig = async () => {
    const response = await AxiosInstance.get('/api/access-config');
    const data = response.data.data;
    setConfig(data);
    setFormData({
      mode: data.mode || 'free',
      price: data.price ?? 0,
      campaignStartDate: data.campaignStartDate ? data.campaignStartDate.slice(0, 10) : '',
      campaignEndDate: data.campaignEndDate ? data.campaignEndDate.slice(0, 10) : '',
      freePreviewStepsCount: data.freePreviewStepsCount ?? 0,
      freeProceduresLimit: data.freeProceduresLimit ?? '',
      freeProceduresPeriod: data.freeProceduresPeriod || 'month',
      message: data.message || '',
      isActive: data.isActive !== false,
    });
  };

  const fetchStats = async () => {
    const response = await AxiosInstance.get('/api/access-config/stats');
    setStats(response.data.data);
  };

  const fetchHistory = async (page = 1) => {
    const response = await AxiosInstance.get(`/api/access-config/history?page=${page}&limit=10`);
    setHistory(response.data.data.entries);
    setHistoryTotalPages(response.data.data.pagination.pages);
    setHistoryPage(page);
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await Promise.all([fetchConfig(), fetchStats()]);
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Erreur lors du chargement de la configuration');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (showHistory) fetchHistory(1);
  }, [showHistory]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.mode === 'paid' && !(Number(formData.price) > 0)) {
      setError('Le prix doit être strictement supérieur à 0 en mode payant.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const payload = {
        mode: formData.mode,
        price: Number(formData.price),
        campaignStartDate: formData.campaignStartDate || null,
        campaignEndDate: formData.campaignEndDate || null,
        freePreviewStepsCount: Number(formData.freePreviewStepsCount),
        freeProceduresLimit: formData.freeProceduresLimit === '' ? null : Number(formData.freeProceduresLimit),
        freeProceduresPeriod: formData.freeProceduresPeriod,
        message: formData.message,
        isActive: formData.isActive,
      };
      await AxiosInstance.put('/api/access-config', payload);
      await fetchConfig();
      setSuccessMessage('Configuration mise à jour avec succès ✅');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleReactivate = async () => {
    try {
      setSaving(true);
      setError('');
      await AxiosInstance.post('/api/access-config/reactivate');
      await fetchConfig();
      setSuccessMessage('Campagne réactivée avec succès ✅');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erreur lors de la réactivation');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading-container"><div className="loading-spinner"></div></div>;

  const campaignExpired = config?.effectiveMode === 'free' && config?.mode === 'paid';

  return (
    <div className="arrondissement-container">
      <div className="container mx-auto px-4 py-4">
        {/* Header */}
        <div className="card page-header">
          <div className="header-content">
            <h1 className="header-title">Gestion de l'accès</h1>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setShowHistory(true)} className="action-button" title="Historique">
                <History size={16} /> Historique
              </button>
              <button onClick={handleReactivate} className="add-button" disabled={saving}>
                <Unlock className="add-button-icon" /> Réactiver la campagne
              </button>
            </div>
          </div>
        </div>

        {/* État : mode configuré vs effectif */}
        <div className="card" style={{ marginBottom: '15px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#666' }}>Mode configuré :</span>
              <ModeBadge mode={config?.mode} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#666' }}>Mode effectif :</span>
              <ModeBadge mode={config?.effectiveMode} />
            </div>
            {campaignExpired && (
              <span style={{ color: '#d97706', fontSize: '14px' }}>
                ⚠️ La campagne configurée est expirée : l'accès est temporairement gratuit (la configuration en base n'est pas modifiée).
              </span>
            )}
          </div>
        </div>

        {/* Formulaire */}
        <div className="card" style={{ marginBottom: '15px' }}>
          <form onSubmit={handleSubmit}>
            {/* Mode d'accès */}
            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label className="form-label">Mode d'accès</label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, mode: 'free' })}
                  className={`mode-toggle ${formData.mode === 'free' ? 'mode-toggle-active-free' : ''}`}
                >
                  Gratuit
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, mode: 'paid' })}
                  className={`mode-toggle ${formData.mode === 'paid' ? 'mode-toggle-active-paid' : ''}`}
                >
                  Payant
                </button>
              </div>
            </div>

            <div className="form-grid">
              {/* Prix (uniquement en mode payant) */}
              {formData.mode === 'paid' && (
                <div className="form-group">
                  <label className="form-label">Prix (FCFA) *</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    required
                    value={formData.price}
                    onChange={e => setFormData({ ...formData, price: e.target.value })}
                    className="form-input"
                    placeholder="Ex : 500"
                  />
                </div>
              )}

              {/* Dates de campagne */}
              <div className="form-group">
                <label className="form-label">Date de début de campagne (optionnel)</label>
                <input
                  type="date"
                  value={formData.campaignStartDate}
                  onChange={e => setFormData({ ...formData, campaignStartDate: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Date de fin de campagne (optionnel)</label>
                <input
                  type="date"
                  value={formData.campaignEndDate}
                  onChange={e => setFormData({ ...formData, campaignEndDate: e.target.value })}
                  className="form-input"
                />
              </div>

              {/* Aperçu gratuit */}
              <div className="form-group">
                <label className="form-label">Nombre d'étapes gratuites (aperçu)</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={formData.freePreviewStepsCount}
                  onChange={e => setFormData({ ...formData, freePreviewStepsCount: e.target.value })}
                  className="form-input"
                />
              </div>

              {/* Limite de consommation gratuite */}
              <div className="form-group">
                <label className="form-label">Limite de procédures gratuites (vide = illimité)</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={formData.freeProceduresLimit}
                  onChange={e => setFormData({ ...formData, freeProceduresLimit: e.target.value })}
                  className="form-input"
                  placeholder="Ex : 3"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Période de limitation</label>
                <select
                  value={formData.freeProceduresPeriod}
                  onChange={e => setFormData({ ...formData, freeProceduresPeriod: e.target.value })}
                  className="form-input"
                >
                  {PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
            </div>

            {/* Message promotionnel */}
            <div className="form-group">
              <label className="form-label">Message promotionnel</label>
              <textarea
                value={formData.message}
                onChange={e => setFormData({ ...formData, message: e.target.value })}
                rows="3"
                className="form-textarea"
                placeholder="Ex : Offre gratuite jusqu'au 30/09/2026 !"
              />
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={e => setFormData({ ...formData, isActive: e.target.checked })}
                  className="form-checkbox"
                />
                Configuration active
              </label>
            </div>

            <div className="modal-actions">
              <button type="submit" className="modal-button submit" disabled={saving}>
                {saving ? 'Enregistrement...' : 'Enregistrer la configuration'}
              </button>
            </div>
          </form>
        </div>

        {/* Statistiques */}
        {stats && (
          <div className="card stats-section">
            <h2 style={{ fontSize: '18px', marginBottom: '15px' }}>Statistiques</h2>
            <div className="stats-grid">
              <div className="stat-item">
                <div className="stat-number blue">{stats.freeViews}</div>
                <div className="stat-label">Consultations complètes gratuites</div>
              </div>
              <div className="stat-item">
                <div className="stat-number green">{stats.paidAccesses}</div>
                <div className="stat-label">Accès payants</div>
              </div>
              <div className="stat-item">
                <div className="stat-number orange">{stats.revenue}</div>
                <div className="stat-label">Revenus (FCFA)</div>
              </div>
              <div className="stat-item">
                <div className="stat-number blue">{stats.conversionRate}%</div>
                <div className="stat-label">Taux de conversion</div>
              </div>
              <div className="stat-item">
                <div className="stat-number green">{stats.usersUsingFreeAccess}</div>
                <div className="stat-label">Utilisateurs en accès gratuit</div>
              </div>
              <div className="stat-item">
                <div className="stat-number orange">{stats.economyGenerated}</div>
                <div className="stat-label">Économie générée (FCFA)</div>
              </div>
            </div>
          </div>
        )}

        {/* Historique */}
        {showHistory && (
          <div className="modal-overlay">
            <div className="modal-content large">
              <div className="modal-header">
                <h2 className="modal-title">Historique des modifications</h2>
                <button onClick={() => setShowHistory(false)} className="modal-close"><X className="modal-close-icon" /></button>
              </div>
              <div className="modal-body">
                <div className="table-scroll">
                  <table className="data-table">
                    <thead className="table-header">
                      <tr>
                        <th>Date</th>
                        <th>Auteur</th>
                        <th>Ancienne configuration</th>
                        <th>Nouvelle configuration</th>
                      </tr>
                    </thead>
                    <tbody className="table-body">
                      {history.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="empty-state">
                            <p className="empty-title">Aucune modification enregistrée</p>
                          </td>
                        </tr>
                      ) : history.map(entry => (
                        <tr key={entry._id} className="table-row">
                          <td className="table-cell">{formatDate(entry.createdAt)}</td>
                          <td className="table-cell">{entry.updatedBy?.name || entry.updatedBy?.email || '—'}</td>
                          <td className="table-cell">
                            <ConfigSummary config={entry.previousConfig} />
                          </td>
                          <td className="table-cell">
                            <ConfigSummary config={entry.newConfig} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {historyTotalPages > 1 && (
                  <div className="pagination-container">
                    <button disabled={historyPage === 1} onClick={() => fetchHistory(historyPage - 1)}>Précédent</button>
                    <span style={{ alignSelf: 'center' }}>Page {historyPage} / {historyTotalPages}</span>
                    <button disabled={historyPage === historyTotalPages} onClick={() => fetchHistory(historyPage + 1)}>Suivant</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {successMessage && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h2 className="modal-title">Succès</h2>
              </div>
              <div className="modal-body"><p>{successMessage}</p></div>
            </div>
          </div>
        )}

        {error && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h2 className="modal-title">Erreur</h2>
                <button className="modal-close" onClick={() => setError('')}><X className="modal-close-icon" /></button>
              </div>
              <div className="modal-body"><p>{error}</p></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ConfigSummary = ({ config }) => {
  if (!config) return <span>—</span>;
  const price = config.mode === 'paid' ? ` — ${config.price} ${config.currency || 'XAF'}` : '';
  return (
    <span style={{ fontSize: '13px' }}>
      <ModeBadge mode={config.mode} /> {price}
      {config.campaignEndDate && <><br />Fin campagne : {formatDate(config.campaignEndDate)}</>}
    </span>
  );
};

export default AccessConfig;
